import { useMemo, useState, type MouseEvent } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ApartmentOutlined,
  CheckOutlined,
  ExpandMoreOutlined,
  SearchOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import { useIsMutating } from '@tanstack/react-query';
import { useBranchContext } from '@/stores/BranchStore';
import type { BranchInfo } from '@/types/auth';

// Show a search box only when the list is long enough to warrant it.
const SEARCH_THRESHOLD = 6;

/**
 * Navbar branch switcher. Shows the current company + branch and, when the user can access more
 * than one branch, a dropdown to switch. Switching persists server-side and refetches branch data
 * (handled by the BranchStore); the whole app never reloads. Responsive: full label on desktop,
 * compact (branch name only) on mobile.
 */
export default function BranchSwitcher() {
  const {
    currentBranch,
    currentCompany,
    availableBranches,
    hasNoActiveBranch,
    isLoading,
    isSwitching,
    error,
    switchBranch,
  } = useBranchContext();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const open = Boolean(anchor);
  const multi = availableBranches.length > 1;
  // A write/AI mutation is in flight (e.g. AI itinerary/quotation generation, saving). Switching branch
  // mid-operation could let the backend persist that operation under a different branch, so block the
  // switch until it settles. The switch's own mutation (isSwitching) is excluded. The backend also
  // enforces branch authorization on every request — this is a safety/UX guard, not the security boundary.
  const criticalBusy = useIsMutating() > 0 && !isSwitching;

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    if (!multi || criticalBusy) return; // single branch, or a critical operation is running
    setAnchor(e.currentTarget);
  };
  const closeMenu = () => {
    setAnchor(null);
    setSearch('');
  };

  const handleSelect = async (id: number) => {
    closeMenu();
    await switchBranch(id);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableBranches;
    return availableBranches.filter(
      (b) => b.name.toLowerCase().includes(q) || b.code.toLowerCase().includes(q),
    );
  }, [availableBranches, search]);

  // ---- Loading / empty / error states -------------------------------------------------
  if (isLoading) {
    return <Skeleton variant="rounded" width={160} height={36} sx={{ borderRadius: 2 }} />;
  }

  if (error) {
    return (
      <Tooltip title={error}>
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<StorefrontOutlined />}
          label="Branches unavailable"
        />
      </Tooltip>
    );
  }

  if (hasNoActiveBranch || !currentBranch) {
    return (
      <Tooltip title="No active branch is available for your account. Please contact your administrator.">
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<StorefrontOutlined />}
          label="No active branch"
        />
      </Tooltip>
    );
  }

  const label = `${currentBranch.name}${currentBranch.code ? ` · ${currentBranch.code}` : ''}`;

  return (
    <>
      <Tooltip title={criticalBusy ? 'Finish the current operation before switching branch' : multi ? 'Switch branch' : currentCompany ?? 'Current branch'}>
        <Box
          component={multi ? 'button' : 'div'}
          onClick={openMenu}
          aria-haspopup={multi ? 'menu' : undefined}
          aria-expanded={multi ? open : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            maxWidth: { xs: 150, sm: 240 },
            px: 1.25,
            py: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            cursor: criticalBusy ? 'not-allowed' : multi ? 'pointer' : 'default',
            font: 'inherit',
            color: 'inherit',
            opacity: criticalBusy ? 0.6 : 1,
            '&:hover': multi && !criticalBusy ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : undefined,
          }}
        >
          <StorefrontOutlined fontSize="small" color="action" />
          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
            {/* The company name is NOT repeated here. It sits at the top of the sidebar, where it
                never changes -- and a name printed twice on one screen is a name nobody reads.
                This slot answers the other question: which branch am I working in, and can I
                change it. */}
            <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.2 }}>
              {label}
            </Typography>
          </Box>
          {isSwitching ? (
            <CircularProgress size={14} sx={{ ml: 0.25 }} />
          ) : (
            multi && <ExpandMoreOutlined fontSize="small" color="action" />
          )}
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={closeMenu}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 280, maxWidth: 340, borderRadius: 3 } } }}
      >
        {currentCompany && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ApartmentOutlined fontSize="small" color="action" />
            <Typography variant="subtitle2" noWrap>
              {currentCompany}
            </Typography>
          </Box>
        )}

        {availableBranches.length > SEARCH_THRESHOLD && (
          <Box sx={{ px: 1.5, py: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search branches…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()} // keep type-ahead out of the Menu's focus handler
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {filtered.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No matching branches" />
          </MenuItem>
        ) : (
          filtered.map((b: BranchInfo) => {
            const selected = b.id === currentBranch.id;
            return (
              <MenuItem key={b.id} selected={selected} onClick={() => handleSelect(b.id)}>
                <ListItemIcon>{selected ? <CheckOutlined fontSize="small" color="primary" /> : <StorefrontOutlined fontSize="small" />}</ListItemIcon>
                <ListItemText
                  primary={b.name}
                  secondary={[b.code, b.isHeadOffice ? 'Head office' : null, 'Active'].filter(Boolean).join(' · ') || undefined}
                  primaryTypographyProps={{ fontWeight: selected ? 700 : 500, noWrap: true }}
                />
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
}
